// ============================================================
//  livePush.js — push notifications for Website Widget chats
//
//  Turns on real push for THIS browser/device: new chats arrive as a system
//  notification (with the system sound) even when CommandKit is closed.
//  Uses the chat app's service worker (/chats-sw.js), so tapping a
//  notification opens CommandKit Chats on that conversation.
// ============================================================
import { livePush, apiBaseUrl } from '../../api/client';

const SW_URL = '/chats-sw.js';
const SCOPE  = '/chats';
export const PUSH_FLAG = 'ck_lc_push';

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

/** 'ok' | 'ios-install' (iPhone: add to Home Screen first) | 'unsupported' */
export function pushSupport() {
  const ok = 'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';
  if (isIOS() && !isStandalone()) return 'ios-install';
  return ok ? 'ok' : 'unsupported';
}

export function pushFlagOn() {
  try { return localStorage.getItem(PUSH_FLAG) === '1'; } catch { return false; }
}
function setFlag(on) {
  try { localStorage.setItem(PUSH_FLAG, on ? '1' : '0'); } catch { /* ignore */ }
}

function keyBytes(b64u) {
  const s = b64u.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(s + '='.repeat((4 - (s.length % 4)) % 4));
  return Uint8Array.from(raw, c => c.charCodeAt(0));
}
function sameKey(buf, b64u) {
  if (!buf) return false;
  const a = new Uint8Array(buf); const b = keyBytes(b64u);
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

function deviceLabel() {
  const ua = navigator.userAgent;
  const os = /iPhone|iPad/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : 'device';
  const br = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  return `${br} on ${os}${isStandalone() ? ' (app)' : ''}`;
}

/** The chat app's service worker, installed if needed and ready to receive pushes. */
async function registration() {
  let reg = await navigator.serviceWorker.getRegistration(SCOPE);
  if (!reg) reg = await navigator.serviceWorker.register(SW_URL, { scope: SCOPE });
  if (reg.active) return reg;
  const sw = reg.installing || reg.waiting;
  await new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('The notification helper didn’t start. Reload and try again.')), 15000);
    sw?.addEventListener('statechange', () => { if (sw.state === 'activated') { clearTimeout(t); resolve(); } });
    if (!sw) { clearTimeout(t); resolve(); }
  });
  return reg;
}

export async function currentSubscription() {
  if (pushSupport() !== 'ok') return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SCOPE);
    return reg ? await reg.pushManager.getSubscription() : null;
  } catch { return null; }
}

/** { support, permission, on, quick, quickDefault, devices } */
export async function pushStatus() {
  const support = pushSupport();
  const permission = typeof Notification === 'undefined' ? 'default' : Notification.permission;
  if (support !== 'ok') return { support, permission, on: false, quick: '', quickDefault: '', devices: [] };
  const sub = await currentSubscription();
  const res = await livePush('key', sub ? { endpoint: sub.endpoint } : {});
  const d = res.data;
  let on = !!(sub && d.device);
  // Self-heal: the browser is subscribed but the server lost it (or the key changed) → sign it up again quietly.
  if (sub && permission === 'granted' && (!d.device || !sameKey(sub.options?.applicationServerKey, d.publicKey))) {
    try { await enablePush(d.device?.quick || ''); on = true; } catch { on = false; }
  }
  setFlag(on);
  return { support, permission, on, quick: d.device?.quick || '', quickDefault: d.quickDefault || '', devices: d.devices || [] };
}

/** Ask permission (must be called from a click), subscribe, and register this device. */
export async function enablePush(quick = '') {
  if (pushSupport() !== 'ok') throw new Error('Push notifications aren’t available in this browser.');
  const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (perm !== 'granted') throw new Error(perm === 'denied'
    ? 'Notifications are blocked for this site — allow them in your browser’s site settings, then try again.'
    : 'You need to allow notifications to turn this on.');
  const reg = await registration();
  const { data } = await livePush('key');
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub.options?.applicationServerKey, data.publicKey)) { await sub.unsubscribe().catch(() => {}); sub = null; }
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(data.publicKey) });
  await livePush('subscribe', {
    subscription: sub.toJSON(), api: apiBaseUrl(), app: `${window.location.origin}${SCOPE}`, label: deviceLabel(), quick,
  });
  setFlag(true);
  return sub;
}

export async function disablePush() {
  const sub = await currentSubscription();
  if (sub) {
    await livePush('unsubscribe', { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  setFlag(false);
}

export const testPush = () => livePush('test');
